import { useEffect, useRef, useCallback } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '@/config'
import api from '@/services/api'
import { shouldProactivelyRefresh } from '@/utils/sessionToken'

const DEFAULT_CUSTOMER_MINUTES = 30
const DEFAULT_ADMIN_MINUTES = 30

let _cachedCustomerMinutes: number | null = null
let _cachedAdminMinutes: number | null = null

async function fetchTimeoutSettings(): Promise<{ customer: number; admin: number }> {
  if (_cachedCustomerMinutes !== null && _cachedAdminMinutes !== null) {
    return { customer: _cachedCustomerMinutes, admin: _cachedAdminMinutes }
  }
  try {
    const data = await api.get('/admin/settings/public').then(r => r.data)
    _cachedCustomerMinutes = parseInt(data?.customer_session_timeout_minutes, 10) || DEFAULT_CUSTOMER_MINUTES
    _cachedAdminMinutes   = parseInt(data?.admin_session_timeout_minutes, 10)    || DEFAULT_ADMIN_MINUTES
  } catch {
    _cachedCustomerMinutes = DEFAULT_CUSTOMER_MINUTES
    _cachedAdminMinutes   = DEFAULT_ADMIN_MINUTES
  }
  return { customer: _cachedCustomerMinutes, admin: _cachedAdminMinutes }
}

// ── Sliding session (the actual root-cause fix) ──────────────────────────────
// The access token has a FIXED lifetime (ACCESS_TOKEN_EXPIRE_MINUTES, 30 min by
// default) that, on its own, does NOT grow when the user is active. That capped
// the real session at the token lifetime regardless of the admin's inactivity
// setting: an admin who set 90 min was still booted around the 30-min token
// boundary the moment a background/API call hit the expired token and the
// reactive 401→refresh didn't cleanly cover the gap.
//
// The fix: while the user is ACTIVE, proactively refresh the access token just
// before it expires. Activity therefore genuinely extends the session at the
// token layer, and the ONLY thing that ends a session is the inactivity timer
// below. Crucially this refresh fires ONLY on real user activity — never on a
// timer — so an idle session still lets the token lapse and still logs out.
const PROACTIVE_THROTTLE_MS = 60_000      // at most one proactive refresh per minute

let _lastProactiveRefresh = 0
let _proactiveRefreshInFlight: Promise<void> | null = null

async function maybeRefreshAccessToken(): Promise<void> {
  // Never touch tokens in a read-only "view-as customer" tab — that per-tab
  // token can't be refreshed and must not disturb the admin's real session.
  if (sessionStorage.getItem('view_as_token')) return

  const now = Date.now()
  if (now - _lastProactiveRefresh < PROACTIVE_THROTTLE_MS) return
  if (_proactiveRefreshInFlight) return

  const token = localStorage.getItem('access_token')
  const refresh = localStorage.getItem('refresh_token')
  if (!token || !refresh) return

  // Only refresh when the token is near expiry (or its expiry can't be read).
  if (!shouldProactivelyRefresh(token, now)) return

  _lastProactiveRefresh = now
  _proactiveRefreshInFlight = axios
    .post(`${API_BASE_URL}/auth/refresh`, { refresh_token: refresh })
    .then((r) => {
      if (r.data?.access_token) localStorage.setItem('access_token', r.data.access_token)
    })
    .catch(() => {
      // A failure here is non-fatal: the reactive interceptor in services/api.ts
      // still handles a genuinely dead session on the next request. We must not
      // log an active user out over a transient refresh blip.
    })
    .finally(() => {
      _proactiveRefreshInFlight = null
    })
  return _proactiveRefreshInFlight
}

/**
 * Monitors user inactivity and calls onTimeout after the configured number
 * of idle minutes. Applies globally — web dashboard, admin panel, and all
 * authenticated routes. Timeout values are read from system_settings so the
 * admin can change them without a frontend deploy.
 *
 * Two things happen on every user interaction (mouse/keyboard/touch/scroll):
 *   1. the idle-logout timer is reset, and
 *   2. the access token is proactively refreshed if it is near expiry,
 * so an ACTIVE session never dies at the fixed token lifetime and only an
 * genuinely idle session (no interaction for the configured duration) logs out.
 *
 * @param isAuthenticated - only active when the user is logged in
 * @param isAdmin         - picks the admin or customer timeout threshold
 * @param onTimeout       - called when the inactivity timer fires
 */
export function useInactivityTimeout(
  isAuthenticated: boolean,
  isAdmin: boolean,
  onTimeout: () => void
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const timeoutMsRef = useRef<number>(DEFAULT_CUSTOMER_MINUTES * 60_000)

  const reset = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(onTimeout, timeoutMsRef.current)
  }, [onTimeout])

  // Fired on every real user interaction: slide the idle timer forward AND keep
  // the access token alive so activity genuinely extends the session.
  const onActivity = useCallback(() => {
    reset()
    // Fire-and-forget; internally throttled and only refreshes near expiry.
    void maybeRefreshAccessToken()
  }, [reset])

  // Load timeout value from backend settings
  useEffect(() => {
    if (!isAuthenticated) return
    fetchTimeoutSettings().then(({ customer, admin }) => {
      timeoutMsRef.current = (isAdmin ? admin : customer) * 60_000
      reset()
    })
  }, [isAuthenticated, isAdmin, reset])

  // Activity event listeners
  useEffect(() => {
    if (!isAuthenticated) {
      if (timerRef.current) clearTimeout(timerRef.current)
      return
    }

    const EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel', 'focus']
    EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }))
    reset()

    return () => {
      EVENTS.forEach(e => window.removeEventListener(e, onActivity))
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [isAuthenticated, onActivity, reset])
}
